import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Button from '@mui/material/Button';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useState, useMemo } from 'react';
import { PipeSpecUploadMenu } from './PipeSpecUpload';
import { ServiceGroupUploadMenu } from './ServiceGroupUpload';
import { PlannerEquipmentUpload } from './PlannerEquipmentUploads/components/PlannerEquipmentUpload';
import { useFeatureFlag, useRouter } from '@/hooks';
import { Stack, Typography } from '@mui/material';
import { Pill } from '@/components/shared/Pill';
import { Flight } from '@mui/icons-material';
import { ReferenceDrawingsUpload } from './ReferenceDrawingsUpload';
import { isBulkReferenceDrawingsOpen } from '@/components/Analysis/state';
import { useSetAtom } from 'jotai';

export const DataFixingUploadsButton = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sgAnchorEl, setSgAnchorEl] = useState<null | HTMLElement>(null);
  const [plannerEquipmentData, setPlannerEquipmentData] = useState<null | HTMLElement>(null);
  const [psAnchorEl, setPsAnchorEl] = useState<null | HTMLElement>(null);
  const router = useRouter();
  const isWorkInProgress = useFeatureFlag('work-in-progress');
  const setReferenceDrawingsOpen = useSetAtom(isBulkReferenceDrawingsOpen);

  const isInspectionPlanner = useMemo(() => {
    return router.query?.viewMode === 'inspectionPlanner';
  }, [router.query?.viewMode]);

  const showPlannerEquipmentUpload = isInspectionPlanner && isWorkInProgress;
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <>
      <Button
        id="import-files-button"
        startIcon={
          <AbyssIcon
            iconName="file-upload-secondary"
            style={{ height: '1.6rem', width: '1.6rem' }}
          />
        }
        endIcon={<ArrowDropDownIcon sx={{ fontSize: '1.6rem !important' }} />}
        variant="outlined"
        onClick={handleClick}
        size="small"
      >
        Import Files
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        sx={{
          minWidth: 200,
          '> *': { minWidth: 200 },
          maxWidth: '36rem',
          width: '100%',
        }}
      >
        <MenuItem onClick={() => setPsAnchorEl(anchorEl)}>
          <ListItemText>Pipe Spec</ListItemText>
          <ChevronRightIcon />
        </MenuItem>

        <MenuItem onClick={() => setSgAnchorEl(anchorEl)}>
          <ListItemText>Service Group</ListItemText>
          <ChevronRightIcon />
        </MenuItem>
        {showPlannerEquipmentUpload && (
          <MenuItem onClick={() => setPlannerEquipmentData(anchorEl)}>
            <ListItemText>Import Equipment Data</ListItemText>
            <ChevronRightIcon />
          </MenuItem>
        )}
        {isWorkInProgress && (
          <MenuItem>
            <ListItemText
              onClick={() => {
                setReferenceDrawingsOpen(true);
              }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography>Reference Drawings</Typography>
                <Pill
                  label="Pilot"
                  status="linked"
                  size="small"
                  state="hover"
                  disabled
                  showIcon
                  icon={<Flight />}
                />
              </Stack>
            </ListItemText>
            <ChevronRightIcon />
          </MenuItem>
        )}
      </Menu>
      <ServiceGroupUploadMenu anchorEl={sgAnchorEl} setAnchorEl={setSgAnchorEl} />
      <PipeSpecUploadMenu anchorEl={psAnchorEl} setAnchorEl={setPsAnchorEl} />
      {showPlannerEquipmentUpload && (
        <PlannerEquipmentUpload
          plannerEquipmentData={plannerEquipmentData}
          setPlannerEquipmentData={setPlannerEquipmentData}
        />
      )}
      <ReferenceDrawingsUpload />
    </>
  );
};
